import React, { useCallback, useMemo } from 'react'
import { LinkButton } from '@/atoms/Button'
import { paths } from '@/constants/paths'
import { Livestream } from '@/services/LivestreamService'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { LivestreamCard } from './LivestreamCard'

interface LivestreamsTabProps {
  livestreamsMedias: Livestream[]
  projectSlug: string
  pagePlacement: number
}

const MAX_LIVESTREAMS_DISPLAY = 10

export const LivestreamsTab: React.FC<LivestreamsTabProps> = ({ livestreamsMedias, projectSlug, pagePlacement }) => {
  const track = useSafeTrack()
  const { t } = useTranslate('watch')

  const handleLivestreamClick = useCallback(
    (livestream: Livestream) => {
      track('Watch Project Livestream Video Clicked', {
        program: livestream.projectSlug,
        asset_id: livestream.guid,
        title: livestream.name,
      })
    },
    [track],
  )

  const handleViewAllClick = useCallback(() => {
    track('Watch Project Livestreams View All Clicked', { project: projectSlug })
  }, [track, projectSlug])

  const displayedLivestreams = useMemo(() => {
    return livestreamsMedias.slice(0, MAX_LIVESTREAMS_DISPLAY)
  }, [livestreamsMedias])

  const hasMoreLivestreams = livestreamsMedias.length > MAX_LIVESTREAMS_DISPLAY

  return (
    <div id="livestreams-section" className="flex flex-col gap-y-10 md:gap-y-6 lg:gap-y-10">
      {displayedLivestreams.map((livestream, index) => (
        <div key={livestream?.guid} onClick={() => handleLivestreamClick(livestream)}>
          <LivestreamCard {...livestream} itemIndex={index} pagePlacement={pagePlacement} />
        </div>
      ))}
      {hasMoreLivestreams && projectSlug && (
        <div className="flex justify-start pt-4">
          <LinkButton
            href={`${paths.watch.index}/${projectSlug}/livestreams`}
            className="mb-16 w-full md:w-fit"
            variant={'white'}
            outline
            onClick={handleViewAllClick}
          >
            {t('viewAllLivestreams', 'View All Livestreams')}
          </LinkButton>
        </div>
      )}
    </div>
  )
}
